/**
 * Auth gate — renders the sign-in screen until the user is signed in AND
 * confirmed on the portal allowlist; renders children otherwise.
 *
 * Sign-in path: full-page OAuth redirect to /api/v1/portal/login.
 * That endpoint redirects to Google's account picker; Google redirects
 * back to /api/v1/portal/login/callback, which mints the session cookie
 * and bounces to /. No GIS, no FedCM, no iframe — works in every
 * browser regardless of adblock / strict cookie policies.
 */
import { type JSX, type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { me, isLoading, error } = useAuth();
  const isSignedIn = Boolean(me?.is_member);
  // OAuth callback bounces here with `?signin_error=...` on Google
  // refusing the auth (user cancelled, hd mismatch, etc.). Surface
  // it once, then strip from the URL so a refresh doesn't keep showing.
  const [oauthError, setOauthError] = (() => {
    if (typeof window === 'undefined') return [null, () => {}] as const;
    const url = new URL(window.location.href);
    const e = url.searchParams.get('signin_error');
    if (e) {
      url.searchParams.delete('signin_error');
      window.history.replaceState({}, '', url.toString());
    }
    return [e, () => {}] as const;
  })();
  void setOauthError;
  const displayError = oauthError ?? error;

  // Loading state — verifying session against /api/v1/me
  if (isLoading) {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={spinnerStyle} />
          <p style={{ marginTop: 16, color: '#555' }}>Verifying Stanford SSO…</p>
        </div>
      </div>
    );
  }

  // Already signed in + on allowlist — render the app
  if (isSignedIn) {
    return <>{children}</>;
  }

  // Either no session, or signed in via Google but email not on allowlist
  const blocked = Boolean(me && !me.is_member);
  return (
    <div style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={brandStyle}>Stanford MCP Gateway</div>
        <h1 style={titleStyle}>{blocked ? 'Access denied' : 'Sign in to the admin console'}</h1>
        <p style={subtitleStyle}>
          {blocked
            ? `Your @stanford.edu account is signed in, but this email isn't on the portal allowlist.`
            : 'Use your @stanford.edu Google account.'}
        </p>

        {blocked && me?.email && (
          <pre style={blockedBoxStyle}>
            Signed in as: <b>{me.email}</b>
          </pre>
        )}

        {displayError && !blocked && displayError !== 'not_authenticated' && (
          <pre style={errorBoxStyle}>
            {displayError === 'forbidden'
              ? 'Your @stanford.edu account is not on the portal allowlist. Contact an admin.'
              : displayError}
          </pre>
        )}

        {!blocked && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              marginTop: 24,
            }}
          >
            <a
              href="/api/v1/portal/login"
              style={{
                ...fallbackBtnStyle(false),
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <GoogleG />
              <span>Continue with Google</span>
            </a>
          </div>
        )}

        {blocked && (
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('sumcp_google_id_token');
              document.cookie = 'sumcp_session=; Path=/; Max-Age=0';
              window.location.reload();
            }}
            style={signOutBtnStyle}
          >
            Use a different account
          </button>
        )}

        <p style={footerStyle}>Stanford MCP Gateway · admin console</p>
      </div>
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f7f5f1',
  padding: 16,
};

const cardStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 14,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  padding: 32,
  maxWidth: 440,
  width: '100%',
  textAlign: 'center',
};

const brandStyle: React.CSSProperties = {
  fontSize: 16,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#8c1515',
  marginBottom: 6,
  fontWeight: 600,
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 22,
  color: '#1c1c1c',
};

const subtitleStyle: React.CSSProperties = {
  color: '#666',
  fontSize: 16,
  margin: '0 0 8px',
};

const blockedBoxStyle: React.CSSProperties = {
  background: '#faf3f3',
  border: '1px solid #e8d5d5',
  borderRadius: 8,
  padding: 12,
  fontSize: 15,
  margin: '16px 0 0',
  color: '#7a3030',
  textAlign: 'left',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff8e6',
  border: '1px solid #f0d9a0',
  borderRadius: 8,
  padding: 10,
  fontSize: 14,
  margin: '12px 0 0',
  textAlign: 'left',
  color: '#7a5a10',
  whiteSpace: 'pre-wrap',
};

const signOutBtnStyle: React.CSSProperties = {
  marginTop: 14,
  padding: '8px 16px',
  background: 'transparent',
  border: '1px solid #ccc',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 15,
  color: '#555',
};

const footerStyle: React.CSSProperties = {
  marginTop: 24,
  fontSize: 13,
  color: '#999',
};

const spinnerStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  border: '3px solid #e0e0e0',
  borderTopColor: '#8c1515',
  borderRadius: '50%',
  margin: '0 auto',
  animation: 'sumcp-spin 0.8s linear infinite',
};

/** Inline Google "G" SVG so we don't pull in another dependency. */
function GoogleG(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

function fallbackBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '10px 20px',
    border: '1px solid #dadce0',
    background: disabled ? '#eee' : '#fff',
    color: disabled ? '#999' : '#3c4043',
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    minWidth: 240,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  };
}
